import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Button,
  Paper,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import MicIcon from "@mui/icons-material/Mic";
import SendIcon from "@mui/icons-material/Send";
import StopIcon from "@mui/icons-material/Stop";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { fetchAllProviders } from "../utils/APIHelper";

const BASE_URL = "https://vivotal-backend.vercel.app/api/v1";

interface Doctor {
  id: number | string;
  name: string;
  specialty?: string;
  avatar?: string;
  email?: string;
  phone?: string;
  clinic_name?: string;
  health_plans?: string[];
  [key: string]: any; // For additional API fields
}

interface Appointment {
  id: string;
  appointment_date: string;
  appointment_time: string;
  status: string;
  provider_id: string;
  provider_name?: string;
  patient_id: string;
}

interface ConversationMessage {
  id: string;
  type: "user" | "assistant";
  text: string;
  timestamp: Date;
}

export const ConciergesLayout = () => {
  const { user } = useAuth();
  const token = (user as any)?.token || localStorage.getItem("token") || "";
  const patientId = (user as any)?.userId || localStorage.getItem("userId") || "";

  const [userName, setUserName] = useState("User");
  const [inputText, setInputText] = useState("");
  const [isListening, setIsListening] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [conversations, setConversations] = useState<ConversationMessage[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  });

  // Dialog states
  const [openBookDialog, setOpenBookDialog] = useState(false);
  const [openRescheduleDialog, setOpenRescheduleDialog] = useState(false);
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [loadingProviders, setLoadingProviders] = useState(true);

  // Form states
  const [selectedDoctor, setSelectedDoctor] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedTime, setSelectedTime] = useState("");
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [cancelReason, setCancelReason] = useState("");
  const [visitType, setVisitType] = useState("IN_PERSON");
  const [reasonForVisit, setReasonForVisit] = useState("");

  const recognitionRef = useRef<any>(null);
  const conversationRef = useRef<HTMLDivElement>(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  // Initialize speech recognition
  useEffect(() => {
    const SpeechRecognition = (globalThis as any).webkitSpeechRecognition || (globalThis as any).SpeechRecognition;
    if (SpeechRecognition) {
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = "en-US";

      recognitionRef.current.onresult = (event: any) => {
        const transcript = Array.from(event.results)
          .map((result: any) => result[0].transcript)
          .join("");
        setInputText(transcript);
        processUserInput(transcript);
      };

      recognitionRef.current.onerror = (event: any) => {
        console.error("Speech recognition error", event.error);
        setSnackbar({
          open: true,
          message: `Speech recognition error: ${event.error}`,
          severity: "error",
        });
        setIsListening(false);
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load providers from API and appointments from API
  useEffect(() => {
    const loadData = async () => {
      try {
        // Load providers from API
        setLoadingProviders(true);
        if (token) {
          try {
            console.log("Loading providers with token:", token.substring(0, 20) + "...");
            const providerData = await fetchAllProviders(token, 100, 1);
            console.log("Provider response:", providerData);
            if (providerData?.data) {
              console.log("Setting doctors:", providerData.data.length);
              setDoctors(providerData.data);
            } else {
              console.warn("No provider data in response:", providerData);
            }
          } catch (error: any) {
            console.error("Failed to load providers:", error.message);
            setSnackbar({
              open: true,
              message: `Failed to load providers: ${error.message}`,
              severity: "error",
            });
          } finally {
            setLoadingProviders(false);
          }
        } else {
          console.warn("No token available for provider loading");
          setLoadingProviders(false);
        }

        // Set user name
        if (user && (user as any).first_name) {
          setUserName((user as any).first_name);
        }

        // Load appointments if token exists
        if (token) {
          try {
            const appointmentRes = await axios.get(
              `${BASE_URL}/appointments?limit=100&page=1`,
              {
                headers: { Authorization: `Bearer ${token}` },
              }
            );
            if (appointmentRes.data?.data) {
              setAppointments(appointmentRes.data.data);
            }
          } catch (error) {
            console.error("Failed to load appointments:", error);
          }
        }
      } catch (error) {
        console.error("Failed to load data:", error);
      }
    };

    loadData();
  }, [token, user]);

  // Auto scroll to latest message
  useEffect(() => {
    if (conversationRef.current) {
      conversationRef.current.scrollTop = conversationRef.current.scrollHeight;
    }
  }, [conversations]);

  // Speak text using Web Speech API
  const speakText = (text: string) => {
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = "en-US";
    utterance.rate = 1;
    utterance.pitch = 1;
    globalThis.speechSynthesis?.speak(utterance);
  };

  // Start listening
  const handleStartListening = () => {
    if (recognitionRef.current) {
      setIsListening(true);
      setInputText("");
      recognitionRef.current.start();
    }
  };

  // Stop listening
  const handleStopListening = () => {
    if (recognitionRef.current) {
      recognitionRef.current.abort();
      setIsListening(false);
    }
  };

  // Process user input
  const processUserInput = async (text: string) => {
    if (!text.trim()) return;

    setIsProcessing(true);

    // Add user message to conversation
    const userMessage: ConversationMessage = {
      id: Date.now().toString(),
      type: "user",
      text: text,
      timestamp: new Date(),
    };

    setConversations((prev) => [...prev, userMessage]);
    setInputText("");

    try {
      // Determine intent
      const lowerText = text.toLowerCase();

      let response = "";
      if (
        lowerText.includes("book") ||
        lowerText.includes("appointment") ||
        lowerText.includes("schedule")
      ) {
        response =
          "I can help you book an appointment. Would you like to select a doctor and date?";
        setOpenBookDialog(true);
      } else if (
        lowerText.includes("reschedule") ||
        lowerText.includes("change") ||
        lowerText.includes("move")
      ) {
        response = "I can help you reschedule your appointment. Let me fetch your appointments.";
        setOpenRescheduleDialog(true);
      } else if (
        lowerText.includes("cancel") ||
        lowerText.includes("delete") ||
        lowerText.includes("remove")
      ) {
        response =
          "I can help you cancel an appointment. Let me fetch your appointments.";
        setOpenCancelDialog(true);
      } else if (lowerText.includes("what can you do") || lowerText.includes("help")) {
        response = `I can help you with:
        1. Book new appointments
        2. Reschedule existing appointments
        3. Cancel appointments
        4. View your appointments
        5. Get health reminders
        6. Medication information
        
        What would you like to do?`;
      } else if (lowerText.includes("my appointments") || lowerText.includes("show appointments")) {
        response = `You have ${appointments.length} appointments scheduled.`;
      } else {
        response = "I understand. How can I assist you further?";
      }

      // Add assistant message
      const assistantMessage: ConversationMessage = {
        id: (Date.now() + 1).toString(),
        type: "assistant",
        text: response,
        timestamp: new Date(),
      };

      setConversations((prev) => [...prev, assistantMessage]);

      // Speak the response
      speakText(response);
    } catch (error: any) {
      console.error("Processing error:", error);
      const errorResponse: ConversationMessage = {
        id: (Date.now() + 1).toString(),
        type: "assistant",
        text: "Sorry, I encountered an error. Please try again.",
        timestamp: new Date(),
      };
      setConversations((prev) => [...prev, errorResponse]);
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle send button
  const handleSendMessage = () => {
    processUserInput(inputText);
  };

  // Book appointment
  const handleBookAppointment = async () => {
    if (!selectedDoctor || !selectedDate || !selectedTime || !reasonForVisit) {
      setSnackbar({
        open: true,
        message: "Please fill in all fields including reason for visit",
        severity: "error",
      });
      return;
    }

    setIsProcessing(true);
    try {
      const doctor = doctors.find((d) => d.id.toString() === selectedDoctor);
      const appointmentTime = selectedTime + ":00";

      const response = await axios.post(
        `${BASE_URL}/appointments`,
        {
          appointment_date: selectedDate,
          appointment_time: appointmentTime,
          provider_id: selectedDoctor,
          patient_id: patientId,
          visit_type: visitType,
          reason_for_visit: reasonForVisit,
          status: "BOOKED",
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data?.success || response.status === 201) {
        const docName = doctor?.name || "Dr.";
        const appointmentResponse = `Great! I've booked your appointment with ${docName} on ${selectedDate} at ${selectedTime}. You'll receive a confirmation shortly.`;

        const assistantMessage: ConversationMessage = {
          id: Date.now().toString(),
          type: "assistant",
          text: appointmentResponse,
          timestamp: new Date(),
        };

        setConversations((prev) => [...prev, assistantMessage]);
        speakText(appointmentResponse);

        setSnackbar({
          open: true,
          message: "Appointment booked successfully!",
          severity: "success",
        });

        setOpenBookDialog(false);
        setSelectedDoctor("");
        setSelectedDate("");
        setSelectedTime("");
        setReasonForVisit("");
        setVisitType("IN_PERSON");

        // Reload appointments
        const appointmentRes = await axios.get(
          `${BASE_URL}/appointments?limit=100&page=1`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (appointmentRes.data?.data) {
          setAppointments(appointmentRes.data.data);
        }
      }
    } catch (error: any) {
      console.error("Book appointment error:", error);
      const errorMsg = error.response?.data?.data?.errorMessage || 
                      error.response?.data?.message || 
                      "Failed to book appointment";
      setSnackbar({
        open: true,
        message: errorMsg,
        severity: "error",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Reschedule appointment
  const handleRescheduleAppointment = async () => {
    if (!selectedAppointment || !selectedDate || !selectedTime) {
      setSnackbar({
        open: true,
        message: "Please select appointment and new date/time",
        severity: "error",
      });
      return;
    }

    setIsProcessing(true);
    try {
      const appointmentTime = selectedTime + ":00";

      await axios.put(
        `${BASE_URL}/appointments/${selectedAppointment.id}`,
        {
          appointment_date: selectedDate,
          appointment_time: appointmentTime,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const response = `Perfect! I've rescheduled your appointment to ${selectedDate} at ${selectedTime}. A confirmation has been sent.`;

      const assistantMessage: ConversationMessage = {
        id: Date.now().toString(),
        type: "assistant",
        text: response,
        timestamp: new Date(),
      };

      setConversations((prev) => [...prev, assistantMessage]);
      speakText(response);

      setSnackbar({
        open: true,
        message: "Appointment rescheduled successfully!",
        severity: "success",
      });

      setOpenRescheduleDialog(false);
      setSelectedAppointment(null);
      setSelectedDate("");
      setSelectedTime("");

      // Reload appointments
      const appointmentRes = await axios.get(
        `${BASE_URL}/appointments?limit=100&page=1`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (appointmentRes.data?.data) {
        setAppointments(appointmentRes.data.data);
      }
    } catch (error: any) {
      console.error("Reschedule appointment error:", error);
      const errorMsg = error.response?.data?.message || "Failed to reschedule appointment";
      setSnackbar({
        open: true,
        message: errorMsg,
        severity: "error",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Cancel appointment
  const handleCancelAppointment = async () => {
    if (!selectedAppointment || !cancelReason) {
      setSnackbar({
        open: true,
        message: "Please select appointment and provide reason",
        severity: "error",
      });
      return;
    }

    setIsProcessing(true);
    try {
      await axios.put(
        `${BASE_URL}/appointments/${selectedAppointment.id}`,
        {
          status: "CANCELLED",
          cancellation_reason: cancelReason,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const response = `Your appointment has been cancelled. Reason: ${cancelReason}. You're free to book a new appointment whenever you're ready.`;

      const assistantMessage: ConversationMessage = {
        id: Date.now().toString(),
        type: "assistant",
        text: response,
        timestamp: new Date(),
      };

      setConversations((prev) => [...prev, assistantMessage]);
      speakText(response);

      setSnackbar({
        open: true,
        message: "Appointment cancelled successfully!",
        severity: "success",
      });

      setOpenCancelDialog(false);
      setSelectedAppointment(null);
      setCancelReason("");

      // Reload appointments
      const appointmentRes = await axios.get(
        `${BASE_URL}/appointments?limit=100&page=1`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (appointmentRes.data?.data) {
        setAppointments(appointmentRes.data.data);
      }
    } catch (error: any) {
      console.error("Cancel appointment error:", error);
      const errorMsg = error.response?.data?.message || "Failed to cancel appointment";
      setSnackbar({
        open: true,
        message: errorMsg,
        severity: "error",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // More options
  const handleMoreOptions = () => {
    const response = `Here are more options I can help with:
    📋 View Medical Records
    💊 Check Medication Schedule
    🏥 Find Nearby Clinics
    👨‍⚕️ View Doctor Profiles
    📱 Emergency Contact
    🔔 Set Health Reminders
    📊 View Health Reports
    💬 Message Your Doctor
    
    Just ask for any of these!`;

    const assistantMessage: ConversationMessage = {
      id: Date.now().toString(),
      type: "assistant",
      text: response,
      timestamp: new Date(),
    };

    setConversations((prev) => [...prev, assistantMessage]);
    speakText(response);
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: "100vh",
        bgcolor: "#f9f9ff",
        display: "flex",
        flexDirection: "column",
        p: 2,
      }}
    >
      {/* Header */}
      <Box sx={{ textAlign: "center", mb: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Hey {userName}! 👋
        </Typography>
        <Typography variant="h6" sx={{ color: "#555", mb: 1 }}>
          Your Personal Health Concierge
        </Typography>
        <Typography sx={{ color: "#777", fontSize: "0.9rem" }}>
          Ready to assist you 24/7
        </Typography>
      </Box>

      {/* Conversation Area */}
      <Paper
        ref={conversationRef}
        elevation={2}
        sx={{
          flex: 1,
          overflow: "auto",
          mb: 2,
          p: 2,
          backgroundColor: "#fff",
          borderRadius: "12px",
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {conversations.length === 0 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              color: "#999",
            }}
          >
            <Typography>Start by typing or speaking to me!</Typography>
          </Box>
        )}

        {conversations.map((msg) => (
          <Box
            key={msg.id}
            sx={{
              display: "flex",
              justifyContent: msg.type === "user" ? "flex-end" : "flex-start",
              mb: 1,
            }}
          >
            <Paper
              sx={{
                maxWidth: "70%",
                p: 2,
                backgroundColor: msg.type === "user" ? "#007bff" : "#e8f5e9",
                color: msg.type === "user" ? "#fff" : "#000",
                borderRadius: "12px",
                wordBreak: "break-word",
                whiteSpace: "pre-wrap",
              }}
            >
              <Typography variant="body2">{msg.text}</Typography>
              <Typography
                variant="caption"
                sx={{
                  opacity: 0.7,
                  display: "block",
                  mt: 0.5,
                }}
              >
                {msg.timestamp.toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </Typography>
            </Paper>
          </Box>
        ))}

        {isProcessing && (
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <CircularProgress size={20} />
            <Typography variant="caption" sx={{ color: "#999" }}>
              Processing...
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Input Section */}
      <Paper
        elevation={3}
        sx={{
          display: "flex",
          alignItems: "center",
          borderRadius: "50px",
          px: 2,
          py: 0.5,
          mb: 2,
          backgroundColor: "#fff",
        }}
      >
        <IconButton size="small">
          <AttachFileIcon />
        </IconButton>
        <TextField
          variant="standard"
          placeholder="Ask me anything or use voice..."
          fullWidth
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSendMessage()}
          InputProps={{
            disableUnderline: true,
            sx: { fontSize: "16px", ml: 1 },
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={
                    isListening ? handleStopListening : handleStartListening
                  }
                  sx={{
                    color: isListening ? "#ff5722" : "#666",
                  }}
                >
                  {isListening ? <StopIcon /> : <MicIcon />}
                </IconButton>
                <IconButton
                  onClick={() => speakText(inputText || "Please enter text to speak")}
                  sx={{ color: "#666", ml: 0.5 }}
                >
                  <VolumeUpIcon />
                </IconButton>
                <IconButton
                  onClick={handleSendMessage}
                  disabled={!inputText.trim() || isProcessing}
                  sx={{
                    bgcolor: "#007bff",
                    color: "#fff",
                    ml: 1,
                    "&:hover": { bgcolor: "#0056d1" },
                  }}
                >
                  <SendIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {/* Quick Action Buttons */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          flexWrap: "wrap",
          justifyContent: "center",
        }}
      >
        <Button
          variant="contained"
          sx={{
            borderRadius: "20px",
            px: 3,
            py: 1,
            textTransform: "none",
            backgroundColor: "#4caf50",
            "&:hover": { backgroundColor: "#388e3c" },
          }}
          onClick={() => setOpenBookDialog(true)}
        >
          📅 Book Appointment
        </Button>
        <Button
          variant="contained"
          sx={{
            borderRadius: "20px",
            px: 3,
            py: 1,
            textTransform: "none",
            backgroundColor: "#ff9800",
            "&:hover": { backgroundColor: "#f57c00" },
          }}
          onClick={() => setOpenRescheduleDialog(true)}
        >
          🔄 Reschedule
        </Button>
        <Button
          variant="contained"
          sx={{
            borderRadius: "20px",
            px: 3,
            py: 1,
            textTransform: "none",
            backgroundColor: "#f44336",
            "&:hover": { backgroundColor: "#d32f2f" },
          }}
          onClick={() => setOpenCancelDialog(true)}
        >
          ❌ Cancel
        </Button>
        <Button
          variant="outlined"
          sx={{
            borderRadius: "20px",
            px: 3,
            py: 1,
            textTransform: "none",
          }}
          onClick={handleMoreOptions}
        >
          ⚙️ More Options
        </Button>
      </Box>

      {/* Book Appointment Dialog */}
      <Dialog open={openBookDialog} onClose={() => setOpenBookDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Book an Appointment</DialogTitle>
        <DialogContent sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          {loadingProviders && (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 4 }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Loading providers...</Typography>
            </Box>
          )}
          
          {!loadingProviders && doctors.length === 0 && <Typography color="error">No providers available. Please try again later.</Typography>}
          
          {!loadingProviders && doctors.length > 0 && (
            <>
              <FormControl fullWidth>
                <InputLabel>Select Doctor</InputLabel>
                <Select value={selectedDoctor} label="Select Doctor" onChange={(e) => setSelectedDoctor(e.target.value)}>
                  {doctors.map((doctor) => (
                    <MenuItem key={doctor.id} value={doctor.id.toString()}>
                      {doctor.name} {doctor.specialty ? `(${doctor.specialty})` : ""}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Appointment Date"
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                slotProps={{ htmlInput: { min: new Date().toISOString().split("T")[0] } }}
                fullWidth
              />
              <TextField
                label="Appointment Time"
                type="time"
                value={selectedTime}
                onChange={(e) => setSelectedTime(e.target.value)}
                fullWidth
              />
              <FormControl fullWidth>
                <InputLabel>Visit Type</InputLabel>
                <Select value={visitType} label="Visit Type" onChange={(e) => setVisitType(e.target.value)}>
                  <MenuItem value="IN_PERSON">In Person</MenuItem>
                  <MenuItem value="VIRTUAL">Virtual</MenuItem>
                  <MenuItem value="PHONE">Phone</MenuItem>
                  <MenuItem value="HOME_VISIT">Home Visit</MenuItem>
                  <MenuItem value="EMERGENCY">Emergency</MenuItem>
                  <MenuItem value="FOLLOW_UP">Follow Up</MenuItem>
                  <MenuItem value="ROUTINE_CHECKUP">Routine Checkup</MenuItem>
                  <MenuItem value="LAB_TEST">Lab Test</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Reason for Visit"
                multiline
                rows={3}
                value={reasonForVisit}
                onChange={(e) => setReasonForVisit(e.target.value)}
                placeholder="Describe your reason for the appointment (5-5000 characters)"
                fullWidth
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenBookDialog(false)}>Cancel</Button>
          {!loadingProviders && doctors.length > 0 && (
            <Button
              onClick={handleBookAppointment}
              variant="contained"
              disabled={isProcessing || !selectedDoctor}
            >
              {isProcessing ? <CircularProgress size={20} /> : "Book"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Reschedule Appointment Dialog */}
      <Dialog
        open={openRescheduleDialog}
        onClose={() => setOpenRescheduleDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reschedule Appointment</DialogTitle>
        <DialogContent sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          <FormControl fullWidth>
            <InputLabel>Select Appointment</InputLabel>
            <Select
              value={selectedAppointment?.id || ""}
              label="Select Appointment"
              onChange={(e) => {
                const apt = appointments.find((a) => a.id === e.target.value);
                setSelectedAppointment(apt || null);
              }}
            >
              {appointments
                .filter(
                  (apt) =>
                    apt.status === "BOOKED" || apt.status === "SCHEDULED"
                )
                .map((apt) => (
                  <MenuItem key={apt.id} value={apt.id}>
                    {apt.appointment_date} at {apt.appointment_time} - {apt.provider_name || apt.provider_id}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          <TextField
            label="New Date"
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            slotProps={{ htmlInput: { min: new Date().toISOString().split("T")[0] } }}
            fullWidth
          />
          <TextField
            label="New Time"
            type="time"
            value={selectedTime}
            onChange={(e) => setSelectedTime(e.target.value)}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRescheduleDialog(false)}>Cancel</Button>
          <Button
            onClick={handleRescheduleAppointment}
            variant="contained"
            disabled={isProcessing}
          >
            {isProcessing ? <CircularProgress size={20} /> : "Reschedule"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Appointment Dialog */}
      <Dialog open={openCancelDialog} onClose={() => setOpenCancelDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Cancel Appointment</DialogTitle>
        <DialogContent sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          <FormControl fullWidth>
            <InputLabel>Select Appointment</InputLabel>
            <Select
              value={selectedAppointment?.id || ""}
              label="Select Appointment"
              onChange={(e) => {
                const apt = appointments.find((a) => a.id === e.target.value);
                setSelectedAppointment(apt || null);
              }}
            >
              {appointments
                .filter(
                  (apt) =>
                    apt.status === "BOOKED" || apt.status === "SCHEDULED"
                )
                .map((apt) => (
                  <MenuItem key={apt.id} value={apt.id}>
                    {apt.appointment_date} at {apt.appointment_time} - {apt.provider_name || apt.provider_id}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          <TextField
            label="Cancellation Reason"
            multiline
            rows={3}
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder="Tell us why you're cancelling..."
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCancelDialog(false)}>Keep</Button>
          <Button
            onClick={handleCancelAppointment}
            variant="contained"
            color="error"
            disabled={isProcessing}
          >
            {isProcessing ? <CircularProgress size={20} /> : "Cancel Appointment"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
